import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowRight, BadgeCheck, Clock, ExternalLink, Loader2, MapPin, MessageSquare, Send, Sparkles, Star,
  Users, DollarSign, Building2, GraduationCap, Award, Check, ChevronLeft, ChevronRight, X, Play,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FreelancerAvatar } from "@/components/FreelancerAvatar";
import { SiteFooter } from "@/components/SiteFooter";
import { logoUrl } from "@/lib/logo-version";
import { ThemeToggle } from "@/components/ThemeToggle";

export const Route = createFileRoute("/portfolio/$username")({
  head: ({ params }) => {
    const url = `https://lumvio.co/portfolio/${params.username}`;
    return {
      meta: [
        { title: `@${params.username} — Freelancer profile on Lumvio` },
        { name: "description", content: `Hire @${params.username} on Lumvio — verified profile, portfolio, tiered services, and protected milestone payments. Start a chat in one click.` },
        { property: "og:title", content: `@${params.username} on Lumvio` },
        { property: "og:description", content: `Browse work, packages, and reviews. Message instantly. Pay safely with Lumvio payment protection.` },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: "https://lumvio.co/__l5e/assets-v1/bbd15e4b-2805-4707-8582-06a259cb86cc/lumvio-og-v3.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "Lumvio verified freelancer profile" },
        { name: "twitter:image", content: "https://lumvio.co/__l5e/assets-v1/bbd15e4b-2805-4707-8582-06a259cb86cc/lumvio-og-v3.png" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "ar", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            url,
            mainEntity: {
              "@type": "Person",
              name: `@${params.username}`,
              alternateName: params.username,
              url,
              worksFor: { "@type": "Organization", name: "Lumvio", url: "https://lumvio.co" },
            },
          }),
        },
      ],
    };
  },

  component: PortfolioPage,
});

// ---------- i18n helper ----------
function useT() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || "en").startsWith("ar");
  return (en: string, arStr: string) => (ar ? arStr : en);
}

type Tier = { name: string; price: number; delivery_days: number; features: string[]; recommended?: boolean };

type Portfolio = {
  profile: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
    portfolio_username: string;
    portfolio_headline: string | null;
    portfolio_bio: string | null;
    portfolio_bio_long: string | null;
    portfolio_skills: string[];
    portfolio_cover_url: string | null;
    portfolio_intro_video_url: string | null;
    portfolio_country_code: string | null;
    portfolio_timezone: string | null;
    portfolio_languages: Array<{ code: string }>;
    portfolio_hourly_rate_usd: number | null;
    portfolio_starting_price_usd: number | null;
    portfolio_availability: "available" | "open" | "busy" | "unavailable";
    portfolio_response_hours: number | null;
    kyc_status: string;
    member_since: string;
  };
  projects: Array<{
    id: string; title: string; description: string | null; cover_url: string | null; external_url: string | null;
    tags: string[]; role: string | null; tools: string[]; outcome: string | null; gallery: string[]; category: string | null;
  }>;
  services: Array<{
    id: string; title: string; description: string | null; price_from: number | null; delivery_days: number | null;
    tiers: Tier[]; thumbnail_url: string | null; category: string | null;
  }>;
  testimonials: Array<{ id: string; client_name: string; client_role: string | null; quote: string; rating: number | null; created_at: string }>;
  experience: Array<{ id: string; kind: "employment" | "education" | "certification"; title: string; org: string | null; start_date: string | null; end_date: string | null; description: string | null; credential_url: string | null }>;
  stats: {
    completed_projects: number;
    total_earnings_usd: number;
    avg_rating: number;
    review_count: number;
    on_time_pct: number | null;
    rating_breakdown: Record<string, number>;
  };
};

function PortfolioPage() {
  const { username } = Route.useParams();
  const { t: tt } = useTranslation();
  const t = useT();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [lightbox, setLightbox] = useState<{ project: Portfolio["projects"][number]; index: number } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["public-portfolio", username],
    queryFn: async (): Promise<Portfolio | null> => {
      const { data, error } = await supabase.rpc("get_public_portfolio", { _username: username });
      if (error) throw error;
      return (data as any) as Portfolio | null;
    },
  });

  async function onStartIntro() {
    if (!user) {
      try { sessionStorage.setItem("lumvio_referral", username); } catch {}
      navigate({ to: "/auth", search: { tab: "signup", role: "client" } });
      return;
    }
    if (profile?.role !== "client") { toast.error(t("Only client accounts can start a chat.", "حسابات العملاء فقط يمكنها بدء محادثة.")); return; }
    const { data: pid, error } = await supabase.rpc("create_intro_project_from_referral", { _username: username });
    if (error) return toast.error(error.message);
    navigate({ to: "/projects/$id/chat", params: { id: pid as string } });
  }

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }
  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="text-6xl">🕵️</div>
        <h1 className="text-2xl font-semibold">{t("Portfolio not found", "الملف غير موجود")}</h1>
        <p className="max-w-md text-muted-foreground">{t("This freelancer hasn't activated a public page yet.", "لم يفعّل هذا المستقل صفحته العامة بعد.")}</p>
        <Button asChild><Link to="/">{t("Back home", "العودة للرئيسية")}</Link></Button>
      </div>
    );
  }

  const { profile: p, projects, services, testimonials, experience, stats } = data;
  const verified = p.kyc_status === "verified";
  const displayName = p.full_name || `@${p.portfolio_username}`;
  const isVideo = (u: string | null | undefined) => !!u && /\.(mp4|webm|mov|quicktime)(\?|$)/i.test(u);

  return (
    <div className="relative min-h-screen bg-background text-foreground pb-24 md:pb-0">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[520px] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(1000px_500px_at_20%_0%,color-mix(in_oklab,var(--primary)_22%,transparent),transparent_60%),radial-gradient(900px_500px_at_85%_10%,color-mix(in_oklab,var(--primary)_16%,transparent),transparent_60%)]" />
      </div>

      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <Link
          to="/"
          onClick={() => {
            if (typeof window !== "undefined" && window.location.pathname === "/") window.location.reload();
          }}
          aria-label="Lumvio — back to home"
          className="group/logo relative flex items-center gap-2.5"
        >
          <div className="relative">
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-[2px] rounded-[12px] opacity-0 blur-[6px] transition-opacity duration-500 group-hover/logo:opacity-100"
              style={{
                background:
                  "conic-gradient(from 0deg, rgba(99,102,241,0.6), rgba(217,70,239,0.5), rgba(45,212,191,0.5), rgba(99,102,241,0.6))",
              }}
            />
            <div className="relative h-9 w-9 overflow-hidden rounded-[10px] bg-gradient-to-br from-white/[0.10] to-white/[0.02] ring-1 ring-border/60 transition-all duration-300 group-hover/logo:ring-primary/40">
              <img
                src={logoUrl()}
                alt="Lumvio logo"
                width={36}
                height={36}
                decoding="async"
                className="h-full w-full object-contain p-1"
              />
            </div>
          </div>
          <span className="text-[13px] sm:text-[15px] font-semibold tracking-tight" translate="no">
            Lumvio
          </span>
        </Link>
        <div className="flex items-center gap-2 text-sm">
          <ThemeToggle />
          {user ? <Button asChild variant="ghost" size="sm"><Link to="/dashboard">{tt("nav.dashboard")}</Link></Button>
                : <Button asChild variant="ghost" size="sm"><Link to="/auth">{tt("common.signIn")}</Link></Button>}
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6">
        <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-card/50 backdrop-blur-xl shadow-[0_30px_80px_-40px_color-mix(in_oklab,var(--primary)_55%,transparent)]">
          <div className="relative h-56 md:h-72 w-full bg-gradient-to-br from-primary/20 via-primary/10 to-transparent">
            {p.portfolio_cover_url && (
              isVideo(p.portfolio_cover_url) ? (
                <video src={p.portfolio_cover_url} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <img src={p.portfolio_cover_url} alt="" loading="eager" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
              )
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
          </div>

          <div className="relative -mt-16 px-6 md:px-10 pb-8">
            <div className="grid gap-6 md:grid-cols-[auto_1fr_auto] md:items-end">
              <div className="ring-4 ring-card rounded-full">
                <FreelancerAvatar path={p.avatar_url} name={displayName} verified={verified} size="lg" />
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-semibold tracking-tight truncate">{displayName}</h1>
                  {verified && <BadgeCheck className="h-5 w-5 text-emerald-500" />}
                  <AvailabilityPill status={p.portfolio_availability} />
                </div>
                {p.portfolio_headline && <p className="mt-1 text-base md:text-lg text-foreground/80">{p.portfolio_headline}</p>}
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  {p.portfolio_country_code && (
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.portfolio_country_code}</span>
                  )}
                  <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {t("Member since", "عضو منذ")} {new Date(p.member_since).getFullYear()}</span>
                  {p.portfolio_response_hours && (
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {t("Replies in", "يرد خلال")} ~{p.portfolio_response_hours}{t("h", "س")}</span>
                  )}
                  {p.portfolio_languages?.length > 0 && (
                    <span>🗣️ {p.portfolio_languages.map((l) => l.code).join(" · ")}</span>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-2 md:items-end">
                <Button size="lg" onClick={onStartIntro} className="rounded-full gap-2 shadow-[0_16px_40px_-14px_color-mix(in_oklab,var(--primary)_75%,transparent)]">
                  <MessageSquare className="h-4 w-4" /> {t("Hire · Message", "وظّف · راسل")} <ArrowRight className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2 text-xs justify-end">
                  <Sparkles className="h-3 w-3 text-primary" /> <span className="text-muted-foreground">{t("Payment protected", "دفعة محمية")}</span>
                </div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-5">
              <StatChip label={t("Rating", "التقييم")} value={stats.review_count ? `${Number(stats.avg_rating).toFixed(1)}★` : "—"} sub={stats.review_count ? `${stats.review_count} ${t(stats.review_count !== 1 ? "reviews" : "review", "تقييم")}` : t("No reviews yet", "لا تقييمات بعد")} />
              <StatChip label={t("Completed", "المكتملة")} value={String(stats.completed_projects)} sub={t("projects", "مشاريع")} />
              <StatChip label={t("Hourly", "الساعة")} value={p.portfolio_hourly_rate_usd ? `$${p.portfolio_hourly_rate_usd}` : "—"} sub={t("per hour", "لكل ساعة")} />
              <StatChip label={t("Starts at", "يبدأ من")} value={p.portfolio_starting_price_usd ? `$${p.portfolio_starting_price_usd}` : "—"} sub={t("per project", "لكل مشروع")} />
              <StatChip label={t("Response", "الرد")} value={p.portfolio_response_hours ? `${p.portfolio_response_hours}${t("h", "س")}` : "—"} sub={t("typical", "المعتاد")} />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-8 max-w-6xl px-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-2 sm:grid-cols-5 h-11 rounded-xl bg-muted/60 p-1">
            <TabsTrigger value="overview" className="rounded-lg text-xs md:text-sm">{t("Overview", "نظرة عامة")}</TabsTrigger>
            <TabsTrigger value="portfolio" className="rounded-lg text-xs md:text-sm">{t("Portfolio", "الأعمال")}</TabsTrigger>
            <TabsTrigger value="services" className="rounded-lg text-xs md:text-sm">{t("Services", "الخدمات")}</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg text-xs md:text-sm">{t("Reviews", "التقييمات")}</TabsTrigger>
            <TabsTrigger value="experience" className="rounded-lg text-xs md:text-sm">{t("Experience", "الخبرة")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="pt-6">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <Card className="border-border/60 bg-card/60 backdrop-blur-xl">
                <CardContent className="p-6 md:p-8">
                  <h2 className="text-lg font-semibold">{t("About", "نبذة")}</h2>
                  {p.portfolio_bio && <p className="mt-3 text-foreground/85 leading-relaxed">{p.portfolio_bio}</p>}
                  {p.portfolio_bio_long && <p className="mt-3 whitespace-pre-line text-foreground/80 leading-relaxed">{p.portfolio_bio_long}</p>}
                  {!p.portfolio_bio && !p.portfolio_bio_long && <p className="mt-3 text-sm text-muted-foreground">{t("No bio yet.", "لا توجد نبذة بعد.")}</p>}

                  {p.portfolio_skills?.length > 0 && (
                    <>
                      <h3 className="mt-8 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("Skills", "المهارات")}</h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {p.portfolio_skills.map((s) => (
                          <span key={s} className="rounded-full border border-border/60 bg-background/60 px-3 py-1 text-xs font-medium">{s}</span>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <div className="space-y-4">
                {projects.slice(0, 3).length > 0 && (
                  <Card className="border-border/60 bg-card/60 backdrop-blur-xl">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold">{t("Featured work", "أعمال مميّزة")}</h3>
                        <button onClick={() => setTab("portfolio")} className="text-xs text-primary inline-flex items-center gap-0.5">
                          {t("View all", "عرض الكل")} <ArrowRight className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {projects.slice(0, 4).map((pr) => (
                          <button key={pr.id} onClick={() => setLightbox({ project: pr, index: 0 })} aria-label={pr.title || t("Open project", "افتح المشروع")} className="group relative aspect-square overflow-hidden rounded-xl bg-muted">
                            {pr.cover_url ? (
                              isVideo(pr.cover_url) ? <video src={pr.cover_url} className="h-full w-full object-cover" muted /> : <img src={pr.cover_url} loading="lazy" decoding="async" alt={pr.title} className="h-full w-full object-cover transition group-hover:scale-105" />
                            ) : <div className="grid h-full place-items-center text-2xl opacity-20">✦</div>}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {p.portfolio_intro_video_url && (
                  <Card className="border-border/60 bg-card/60 backdrop-blur-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold">{t("Intro video", "فيديو تعريفي")}</h3>
                      <video src={p.portfolio_intro_video_url} controls playsInline className="mt-3 aspect-video w-full rounded-xl bg-black" />
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="portfolio" className="pt-6">
            {projects.length === 0 ? (
              <PublicEmpty icon="🖼️" title={t("No work samples yet", "لا توجد أعمال بعد")} body={t("This freelancer hasn't uploaded projects.", "لم يرفع هذا المستقل أي مشاريع.")} />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 auto-rows-[180px]">
                {projects.map((pr, i) => {
                  const big = i % 5 === 0;
                  return (
                    <button key={pr.id} onClick={() => setLightbox({ project: pr, index: 0 })} aria-label={pr.title || t("Open project", "افتح المشروع")}
                      className={`group relative overflow-hidden rounded-2xl border border-border/60 bg-card/60 transition hover:-translate-y-0.5 hover:shadow-[0_20px_60px_-20px_color-mix(in_oklab,var(--primary)_45%,transparent)] ${big ? "col-span-2 row-span-2" : ""}`}>
                      {pr.cover_url ? (
                        isVideo(pr.cover_url) ? (
                          <video src={pr.cover_url} muted loop playsInline
                            onMouseEnter={(e) => (e.currentTarget as HTMLVideoElement).play()}
                            onMouseLeave={(e) => (e.currentTarget as HTMLVideoElement).pause()}
                            className="h-full w-full object-cover" />
                        ) : (
                          <img src={pr.cover_url} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" alt={pr.title} />
                        )
                      ) : <div className="grid h-full place-items-center text-4xl opacity-20">✦</div>}
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 text-start">
                        <div className="text-xs font-medium text-white/90 line-clamp-1">{pr.title}</div>
                        {pr.category && <div className="text-[10px] uppercase tracking-wider text-white/60">{pr.category}</div>}
                      </div>
                      {isVideo(pr.cover_url) && (
                        <div className="absolute end-2 top-2 rounded-full bg-black/60 p-1.5 backdrop-blur">
                          <Play className="h-3 w-3 text-white fill-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="services" className="pt-6">
            {services.length === 0 ? (
              <PublicEmpty icon="📦" title={t("No services published", "لا خدمات منشورة")} body={t("Message directly to discuss a custom project.", "راسل المستقل مباشرة لمناقشة مشروع مخصّص.")} />
            ) : (
              <div className="space-y-6">
                {services.map((s) => (
                  <Card key={s.id} className="border-border/60 bg-card/60 backdrop-blur-xl overflow-hidden">
                    <CardContent className="p-6 md:p-8">
                      <div className="grid gap-6 md:grid-cols-[auto_1fr]">
                        {s.thumbnail_url && (
                          <div className="w-full md:w-56 aspect-video overflow-hidden rounded-xl bg-muted">
                            <img src={s.thumbnail_url} loading="lazy" decoding="async" className="h-full w-full object-cover" alt={s.title} />
                          </div>
                        )}
                        <div className="min-w-0">
                          {s.category && <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">{s.category}</div>}
                          <h3 className="mt-1 text-xl font-semibold">{s.title}</h3>
                          {s.description && <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.description}</p>}
                        </div>
                      </div>

                      {s.tiers?.length > 0 ? (
                        <div className="mt-6 grid gap-3 md:grid-cols-3">
                          {s.tiers.map((tr, i) => (
                            <div key={i} className={`relative rounded-2xl border p-5 ${tr.recommended ? "border-primary/60 bg-primary/5 shadow-[0_20px_60px_-30px_color-mix(in_oklab,var(--primary)_60%,transparent)]" : "border-border/60 bg-background/40"}`}>
                              {tr.recommended && (
                                <div className="absolute -top-2 start-4 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">{t("Recommended", "موصى بها")}</div>
                              )}
                              <div className="text-sm font-semibold">{tr.name}</div>
                              <div className="mt-2 text-2xl font-semibold tabular-nums">${tr.price}</div>
                              <div className="text-xs text-muted-foreground">{t("Delivery in", "تسليم خلال")} {tr.delivery_days}{t("d", " يوم")}</div>
                              {tr.features?.length > 0 && (
                                <ul className="mt-3 space-y-1.5 text-xs">
                                  {tr.features.map((f, j) => (
                                    <li key={j} className="flex items-start gap-1.5">
                                      <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                                      <span>{f}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <Button size="sm" variant={tr.recommended ? "default" : "secondary"} className="mt-4 w-full rounded-full" onClick={onStartIntro}>
                                {t("Request", "اطلب")} · ${tr.price}
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-6 flex items-end justify-between rounded-xl border border-border/60 p-4">
                          <div>
                            {s.price_from != null && <div className="text-2xl font-semibold tabular-nums">${s.price_from}<span className="text-sm font-normal text-muted-foreground"> {t("from", "بدءًا من")}</span></div>}
                            {s.delivery_days != null && <div className="text-xs text-muted-foreground">{t("Delivery in", "تسليم خلال")} {s.delivery_days}{t("d", " يوم")}</div>}
                          </div>
                          <Button size="sm" onClick={onStartIntro} className="rounded-full gap-2"><MessageSquare className="h-3.5 w-3.5" /> {t("Discuss", "ناقش")}</Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reviews" className="pt-6">
            <ReviewsTab stats={stats} testimonials={testimonials} />
            <div className="mt-8"><LeaveReviewCard username={username} /></div>
          </TabsContent>

          <TabsContent value="experience" className="pt-6">
            {experience.length === 0 ? (
              <PublicEmpty icon="🎓" title={t("No experience listed", "لا خبرات مذكورة")} body={t("This freelancer hasn't added employment or education yet.", "لم يُضِف هذا المستقل خبرات عمل أو دراسة بعد.")} />
            ) : (
              <div className="grid gap-3 max-w-3xl">
                {experience.map((x) => (
                  <div key={x.id} className="flex gap-3 rounded-2xl border border-border/60 bg-card/60 p-5">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                      {x.kind === "employment" ? <Building2 className="h-5 w-5" /> : x.kind === "education" ? <GraduationCap className="h-5 w-5" /> : <Award className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{x.title}</div>
                      <div className="text-xs text-muted-foreground">{x.org} · {fmtRange(x.start_date, x.end_date, t)}</div>
                      {x.description && <p className="mt-1 text-sm">{x.description}</p>}
                      {x.credential_url && (
                        <a href={x.credential_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-primary">
                          {t("View credential", "عرض الشهادة")} <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>

      <section className="mx-auto mt-16 max-w-6xl px-6">
        <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-primary/15 via-card/60 to-card/60 p-10 text-center backdrop-blur-xl">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">{t(`Ready to work with ${p.full_name?.split(" ")[0] || `@${p.portfolio_username}`}?`, `جاهز للعمل مع ${p.full_name?.split(" ")[0] || `@${p.portfolio_username}`}؟`)}</h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">{t("Message directly — pay safely later with Lumvio payment protection.", "راسل مباشرة — وادفع لاحقًا بأمان عبر حماية المدفوعات في لومڤيو.")}</p>
          <Button size="lg" onClick={onStartIntro} className="mt-6 rounded-full gap-2">
            <MessageSquare className="h-4 w-4" /> {t("Start conversation", "ابدأ محادثة")}
          </Button>
        </div>
      </section>

      <div className="mt-16"><SiteFooter /></div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 p-3 backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-6xl items-center gap-2">
          <div className="min-w-0 flex-1 text-xs">
            <div className="font-semibold truncate">{displayName}</div>
            <div className="text-muted-foreground">{p.portfolio_starting_price_usd ? `${t("From", "بدءًا من")} $${p.portfolio_starting_price_usd}` : t("Discuss pricing", "ناقش السعر")}</div>
          </div>
          <Button size="sm" onClick={onStartIntro} className="rounded-full gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" /> {t("Hire · Message", "وظّف · راسل")}
          </Button>
        </div>
      </div>

      <Lightbox state={lightbox} onClose={() => setLightbox(null)} onNav={(dir) => {
        if (!lightbox) return;
        const items = [lightbox.project.cover_url, ...(lightbox.project.gallery || [])].filter(Boolean) as string[];
        const next = (lightbox.index + dir + items.length) % items.length;
        setLightbox({ ...lightbox, index: next });
      }} onCTA={onStartIntro} />
    </div>
  );
}

// ---------- Bits ----------
function StatChip({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-background/60 p-3 backdrop-blur">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] text-muted-foreground">{sub}</div>
    </div>
  );
}

function AvailabilityPill({ status }: { status: Portfolio["profile"]["portfolio_availability"] }) {
  const t = useT();
  const cfg = {
    available: { c: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20", label: t("Available now", "متاح الآن"), dot: "bg-emerald-500" },
    open:      { c: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20", label: t("Open to work", "مستعد للعمل"), dot: "bg-amber-500" },
    busy:      { c: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20", label: t("Busy but open", "مشغول لكن متاح"), dot: "bg-orange-500" },
    unavailable: { c: "bg-muted text-muted-foreground border-border/60", label: t("Not accepting", "لا يقبل عملًا"), dot: "bg-muted-foreground" },
  }[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cfg.c}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot} ${status === "available" ? "animate-pulse" : ""}`} />
      {cfg.label}
    </span>
  );
}

function PublicEmpty({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="rounded-3xl border-2 border-dashed border-border/60 bg-muted/20 p-12 text-center">
      <div className="text-5xl">{icon}</div>
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function ReviewsTab({ stats, testimonials }: { stats: Portfolio["stats"]; testimonials: Portfolio["testimonials"] }) {
  const t = useT();
  const total = stats.review_count || testimonials.length;
  const avg = Number(stats.avg_rating) || 0;
  const breakdown = [5, 4, 3, 2, 1].map((r) => ({ r, c: Number((stats.rating_breakdown as any)?.[r] ?? testimonials.filter((x) => x.rating === r).length) }));

  return (
    <div className="space-y-6">
      <Card className="border-border/60 bg-card/60 backdrop-blur-xl">
        <CardContent className="p-6 grid gap-6 md:grid-cols-[auto_1fr]">
          <div className="text-center">
            <div className="text-5xl font-semibold tabular-nums">{total ? avg.toFixed(1) : "—"}</div>
            <div className="mt-1 flex justify-center gap-0.5 text-amber-500">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < Math.round(avg) ? "fill-current" : "opacity-30"}`} />)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{total} {t(total !== 1 ? "reviews" : "review", "تقييم")}</div>
          </div>
          <div className="space-y-1.5">
            {breakdown.map(({ r, c }) => (
              <div key={r} className="flex items-center gap-2 text-xs">
                <span className="w-6 tabular-nums">{r}★</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-amber-500 transition-all" style={{ width: `${total ? (c / total) * 100 : 0}%` }} />
                </div>
                <span className="w-8 text-end tabular-nums text-muted-foreground">{c}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {testimonials.length === 0 ? (
        <PublicEmpty icon="⭐" title={t("No reviews yet", "لا تقييمات بعد")} body={t("Be the first to work with this freelancer.", "كن أول من يعمل مع هذا المستقل.")} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {testimonials.map((tm) => (
            <div key={tm.id} className="rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur-xl">
              <div className="flex items-center justify-between">
                {tm.rating != null && (
                  <div className="flex gap-0.5 text-amber-500">
                    {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < tm.rating! ? "fill-current" : "opacity-25"}`} />)}
                  </div>
                )}
                <span className="text-[10px] text-muted-foreground">{new Date(tm.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</span>
              </div>
              <blockquote className="mt-2 text-sm leading-relaxed">"{tm.quote}"</blockquote>
              <div className="mt-3 text-xs">
                <div className="font-semibold">{tm.client_name}</div>
                {tm.client_role && <div className="text-muted-foreground">{tm.client_role}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Lightbox({ state, onClose, onNav, onCTA }: {
  state: { project: Portfolio["projects"][number]; index: number } | null;
  onClose: () => void;
  onNav: (dir: number) => void;
  onCTA: () => void;
}) {
  const t = useT();
  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, onClose, onNav]);

  if (!state) return null;
  const items = [state.project.cover_url, ...(state.project.gallery || [])].filter(Boolean) as string[];
  const current = items[state.index] ?? state.project.cover_url;
  const isVideo = current && /\.(mp4|webm|mov|quicktime)(\?|$)/i.test(current);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl p-0 bg-background/95 backdrop-blur-xl border-border/60 overflow-hidden">
        <div className="grid md:grid-cols-[1fr_320px]">
          <div className="relative bg-black flex items-center justify-center min-h-[300px] md:min-h-[520px]">
            {current ? (isVideo ? <video src={current} controls autoPlay className="max-h-[80vh] w-full object-contain" /> : <img src={current} className="max-h-[80vh] w-full object-contain" alt={state.project.title} />) : null}
            {items.length > 1 && (
              <>
                <button onClick={() => onNav(-1)} aria-label={t("Previous image", "الصورة السابقة")} className="absolute start-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white backdrop-blur"><ChevronLeft className="h-5 w-5 rtl:hidden" /><ChevronRight className="h-5 w-5 hidden rtl:block" /></button>
                <button onClick={() => onNav(1)} aria-label={t("Next image", "الصورة التالية")} className="absolute end-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white backdrop-blur"><ChevronRight className="h-5 w-5 rtl:hidden" /><ChevronLeft className="h-5 w-5 hidden rtl:block" /></button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2.5 py-1 text-[10px] text-white backdrop-blur">{state.index + 1} / {items.length}</div>
              </>
            )}
            <button onClick={onClose} aria-label={t("Close", "إغلاق")} className="absolute end-3 top-3 rounded-full bg-black/60 p-2 text-white backdrop-blur"><X className="h-4 w-4" /></button>
          </div>
          <div className="p-6 space-y-3 max-h-[80vh] overflow-y-auto">
            {state.project.category && <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">{state.project.category}</div>}
            <h3 className="text-lg font-semibold">{state.project.title}</h3>
            {state.project.description && <p className="text-sm text-muted-foreground">{state.project.description}</p>}
            {state.project.role && <MetaRow label={t("Role", "الدور")} value={state.project.role} />}
            {state.project.tools?.length > 0 && <MetaRow label={t("Tools", "الأدوات")} value={state.project.tools.join(", ")} />}
            {state.project.outcome && <MetaRow label={t("Outcome", "النتيجة")} value={state.project.outcome} />}
            {state.project.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-2">
                {state.project.tags.map((tg) => <Badge key={tg} variant="secondary" className="text-[10px] rounded-full">{tg}</Badge>)}
              </div>
            )}
            {state.project.external_url && (
              <Button asChild variant="outline" className="w-full rounded-full">
                <a href={state.project.external_url} target="_blank" rel="noreferrer">{t("Visit live project", "زيارة المشروع مباشرة")} <ExternalLink className="ms-1.5 h-3 w-3" /></a>
              </Button>
            )}
            <Button onClick={onCTA} className="w-full rounded-full gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> {t("Hire this freelancer", "وظّف هذا المستقل")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-xs">
      <div className="uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-foreground/90">{value}</div>
    </div>
  );
}

function LeaveReviewCard({ username }: { username: string }) {
  const t = useT();
  const { user, profile } = useAuth();
  const qc = useQueryClient();
  const [quote, setQuote] = useState("");
  const [role, setRole] = useState("");
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  const { data: canReview, isLoading } = useQuery({
    queryKey: ["can-review-portfolio", username, user?.id ?? "anon"],
    queryFn: async () => {
      if (!user) return false;
      const { data, error } = await supabase.rpc("can_review_portfolio", { _username: username });
      if (error) throw error;
      return Boolean(data);
    },
  });

  if (!user) {
    return (
      <Card className="border-dashed border-border/60 bg-card/40">
        <CardContent className="p-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 font-medium text-foreground"><Star className="h-4 w-4 text-primary" /> {t("Worked with this freelancer?", "عملت مع هذا المستقل؟")}</div>
          <p className="mt-1">{t("Sign in as a client to leave a review after your project is completed.", "سجّل دخولك كعميل لترك تقييم بعد إنهاء مشروعك.")}</p>
          <Button asChild variant="outline" className="mt-4 rounded-full"><Link to="/auth" search={{ tab: "signin" }}>{t("Sign in", "تسجيل الدخول")}</Link></Button>
        </CardContent>
      </Card>
    );
  }
  if (isLoading) return <Card><CardContent className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t("Checking…", "جارٍ التحقق…")}</CardContent></Card>;
  if (!canReview) {
    return (
      <Card className="border-dashed border-border/60 bg-card/40">
        <CardContent className="p-6 text-sm text-muted-foreground space-y-2">
          <div className="flex items-center gap-2 font-medium text-foreground"><Star className="h-4 w-4 text-primary" /> {t("Reviews are verified", "التقييمات موثّقة")}</div>
          <p>{t("Only clients who completed a project with this freelancer on Lumvio can leave a review.", "يمكن للعملاء الذين أنهوا مشروعًا مع هذا المستقل على لومڤيو فقط ترك تقييم.")}{profile?.role === "freelancer" && t(" Freelancers can't review themselves.", " لا يمكن للمستقلين تقييم أنفسهم.")}</p>
          {profile?.role !== "freelancer" && user?.email && (
            <p className="text-xs">
              {t("You're signed in as ", "أنت مسجّل الدخول بـ ")}<span className="font-medium text-foreground">{user.email}</span>{t(". Make sure this matches the email address the freelancer used on your project — or that the project is marked completed.", ". تأكد أن هذا البريد يطابق البريد الذي استخدمه المستقل في مشروعك — وأن المشروع مُعلَّم كمُنجَز.")}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }


  async function submit() {
    if (quote.trim().length < 10) return toast.error(t("Please write at least a full sentence (10+ characters).", "الرجاء كتابة جملة كاملة على الأقل (10 أحرف أو أكثر)."));
    setSubmitting(true);
    const { error } = await supabase.rpc("submit_portfolio_review", { _username: username, _quote: quote.trim(), _rating: rating, _client_role: role.trim() || undefined });
    setSubmitting(false);
    if (error) {
      const map: Record<string, string> = {
        NOT_ELIGIBLE: t("Only completed-project clients can review.", "يمكن للعملاء الذين أنهوا مشروعًا فقط التقييم."),
        ALREADY_REVIEWED: t("You've already reviewed.", "لقد قيّمت بالفعل."),
        QUOTE_TOO_SHORT: t("Please write more.", "الرجاء كتابة المزيد."),
        INVALID_RATING: t("Rating must be 1–5.", "التقييم يجب أن يكون بين 1 و 5."),
        AUTH_REQUIRED: t("Please sign in.", "الرجاء تسجيل الدخول."),
      };
      const key = Object.keys(map).find((k) => error.message.includes(k));
      return toast.error(key ? map[key] : error.message);
    }
    toast.success(t("Thanks — review live!", "شكرًا — التقييم منشور!"));
    setQuote(""); setRole(""); setRating(5);
    qc.invalidateQueries({ queryKey: ["public-portfolio", username] });
    qc.invalidateQueries({ queryKey: ["can-review-portfolio", username, user?.id ?? "anon"] });
  }

  return (
    <Card className="border-border/60 bg-gradient-to-br from-card to-primary/5">
      <CardContent className="p-6 md:p-8 space-y-4">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold"><Star className="h-4 w-4 text-primary" /> {t("Leave a review", "اكتب تقييمًا")}</div>
          <p className="text-xs text-muted-foreground">{t("Your public review helps future clients decide.", "تقييمك العلني يساعد العملاء القادمين في اتخاذ القرار.")}</p>
        </div>
        <Input placeholder={t("Your role / company (optional)", "دورك / شركتك (اختياري)")} value={role} onChange={(e) => setRole(e.target.value)} maxLength={80} />
        <Textarea placeholder={t("What was it like working together? What did they deliver?", "كيف كانت تجربة العمل معًا؟ ماذا قدّم لك؟")} value={quote} onChange={(e) => setQuote(e.target.value)} rows={4} maxLength={2000} />
        <div className="flex items-center justify-between">
          <div className="flex gap-0.5">
            {[1,2,3,4,5].map((n) => (
              <button key={n} onClick={() => setRating(n)} aria-label={`${n} ${t("stars", "نجوم")}`} className="p-1">
                <Star className={`h-6 w-6 ${n <= rating ? "fill-amber-500 text-amber-500" : "text-muted-foreground/40"}`} />
              </button>
            ))}
          </div>
          <Button onClick={submit} disabled={submitting} className="rounded-full gap-1.5">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Submit", "إرسال")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function fmtRange(a: string | null, b: string | null, t: (en: string, ar: string) => string) {
  const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : t("Present", "حتى الآن");
  if (!a && !b) return "";
  return `${fmt(a)} – ${fmt(b)}`;
}
